import React, { useState, useEffect } from 'react';
import PhoneInput, { Country, isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

interface PhoneFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  onBlur?: () => void;
  disabled?: boolean;
  defaultCountry?: Country;
  variant?: 'dark' | 'light';
}

export default function PhoneField({ value, onChange, error, placeholder, onBlur, disabled, defaultCountry, variant = 'dark' }: PhoneFieldProps) {
  // Tick resolves React 18 StrictMode double-invoke: remount once after settle.
  const [tick, setTick] = useState(0);
  useEffect(() => { setTick(1); }, []);

  // Controlled country so the flag stays stable while the user types.
  // When the address country dropdown changes (new defaultCountry prop) and the
  // phone field is still empty, sync the flag to the new country.
  const [country, setCountry] = useState<Country | undefined>(defaultCountry ?? 'US');
  useEffect(() => {
    if (!value && defaultCountry) setCountry(defaultCountry);
  }, [defaultCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inline validation on blur so errors show without waiting for form submit.
  const [blurError, setBlurError] = useState('');
  const handleBlur = () => {
    if (value && !isValidPhoneNumber(value)) {
      setBlurError('Enter a valid phone number with country code');
    } else {
      setBlurError('');
    }
    onBlur?.();
  };

  const displayError = error || blurError;
  return (
    <div>
      <PhoneInput
        key={tick}
        country={country}
        onCountryChange={c => { if (c) setCountry(c); }}
        value={value}
        onChange={val => { onChange(val || ''); setBlurError(''); }}
        onBlur={handleBlur}
        placeholder={placeholder || 'Phone number'}
        className={`phone-field${variant === 'light' ? ' phone-field--light' : ''}${displayError ? ' phone-field--error' : ''}${disabled ? ' opacity-60 cursor-not-allowed' : ''}`}
        disabled={disabled}
      />
      {displayError && <p className="text-red-400 text-xs mt-1">{displayError}</p>}
    </div>
  );
}
